/**
 * ClientCard - Tarjeta compacta de cliente para listados
 * Muestra: nombre, plan, último check-in, peso, estado
 */
import { Crown, User, Scale, Clock, AlertCircle, CheckCircle } from "lucide-react";
import { cn } from "../../lib/utils";
import { Badge } from "../ui/badge";

const ClientCard = ({
  client,
  onClick,
  compact = false
}) => {
  const {
    id,
    name,
    email,
    is_premium,
    is_active,
    has_pending_checkin,
    latest_checkin,
    current_weight,
    weight_change,
    level
  } = client;

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "Sin check-in";
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Hoy";
    if (diffDays === 1) return "Ayer";
    if (diffDays < 7) return `Hace ${diffDays} días`;
    if (diffDays < 30) return `Hace ${Math.floor(diffDays / 7)} sem`;
    return date.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
  };

  return (
    <div
      onClick={() => onClick?.(client)}
      className={cn(
        "p-4 rounded-xl bg-[#121212] border border-[#2A2A2A] transition-all duration-200",
        onClick && "cursor-pointer hover:bg-[#1A1A1A] hover:border-[#3A3A3A] active:scale-[0.99]",
        has_pending_checkin && "border-l-2 border-l-[#D4AF37]"
      )}
      data-testid={`client-card-${id}`}
    >
      <div className="flex items-center gap-3">
        {/* Avatar */}
        <div className={cn(
          "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
          is_premium ? "bg-[#D4AF37]/20" : "bg-[#2A2A2A]"
        )}>
          {is_premium ? (
            <Crown className="w-5 h-5 text-[#D4AF37]" />
          ) : (
            <User className="w-5 h-5 text-[#666]" />
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-white truncate">{name}</p>
            {has_pending_checkin && (
              <span className="w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse flex-shrink-0" />
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <Badge 
              variant="outline" 
              className={cn(
                "text-[10px] px-1.5 py-0 h-4 border-0",
                is_premium ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "bg-[#2A2A2A] text-[#888]"
              )}
            >
              {is_premium ? "Premium" : "Free"}
            </Badge>
            {level && (
              <span className="text-[10px] text-[#555] capitalize">{level}</span>
            )}
          </div>
        </div>

        {/* Right side - Stats */}
        {!compact && (
          <div className="text-right flex-shrink-0">
            {current_weight ? (
              <div className="flex items-center gap-1 justify-end">
                <Scale className="w-3 h-3 text-[#666]" />
                <span className="text-sm font-semibold text-white">{current_weight}kg</span>
                {weight_change !== null && weight_change !== undefined && (
                  <span className={cn(
                    "text-xs",
                    weight_change < 0 ? "text-green-400" : weight_change > 0 ? "text-red-400" : "text-[#666]"
                  )}>
                    ({weight_change > 0 ? "+" : ""}{weight_change})
                  </span>
                )}
              </div>
            ) : (
              <span className="text-xs text-[#555]">Sin peso</span>
            )}
            <div className="flex items-center gap-1 justify-end mt-1">
              <Clock className="w-3 h-3 text-[#555]" />
              <span className={cn(
                "text-[10px]",
                !is_active && latest_checkin ? "text-orange-400" : "text-[#555]"
              )}>
                {formatDate(latest_checkin?.date)}
              </span>
            </div>
          </div>
        )}

        {/* Status indicator */}
        <div className="flex-shrink-0">
          {has_pending_checkin ? (
            <AlertCircle className="w-5 h-5 text-[#D4AF37]" />
          ) : is_active ? (
            <CheckCircle className="w-5 h-5 text-green-500/50" />
          ) : (
            <div className="w-5 h-5 rounded-full bg-[#2A2A2A]" />
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientCard;
